import { useMemo } from "react"
import { DateTime } from "luxon"
import { roundDateTimeToNearestMinutes } from "../../../../resource/resourceBookingCalendars/helpers"
import { useDateTime } from "../../../../../../../../modules/localization/useDateTime"
import { DeliveryTimeDay } from "../../../../form/formTypes"
import { getOpeningLimitsForDate } from "../../helpers"

export type MinMaxTimeLimitsProps = {
    minTime: DateTime
    maxTime: DateTime | null
} | null

export const useDeliveryTimeLimits = (deliveryDate: DateTime | null, openingHours: DeliveryTimeDay[]) => {
    const dateTimeFactory = useDateTime()

    const now = dateTimeFactory.now()

    const isToday = useMemo(() => {
        return !!deliveryDate && deliveryDate.hasSame(now, "day")
    }, [deliveryDate?.toISO(), now.toISO()])

    const limits = useMemo(() => {
        return deliveryDate ? getOpeningLimitsForDate(deliveryDate, openingHours) : null
    }, [deliveryDate?.toISO(), openingHours])

    const minMaxTime: MinMaxTimeLimitsProps = useMemo(() => {
        if (!deliveryDate) return null

        if (!limits) {
            if (isToday) {
                const roundedNow = roundDateTimeToNearestMinutes(now, 5)
                return { minTime: roundedNow, maxTime: null }
            }
            return null
        }

        if (isToday) {
            const roundedNow = roundDateTimeToNearestMinutes(now, 5)
            const min = roundedNow > limits.minTime ? roundedNow : limits.minTime
            return { minTime: min, maxTime: limits.maxTime }
        }

        return { minTime: limits.minTime, maxTime: limits.maxTime }
    }, [deliveryDate?.toISO(), limits?.minTime.toISO(), limits?.maxTime.toISO(), isToday, now.toISO()])

    const noSlotsForDate = useMemo(() => {
        if (!deliveryDate || !limits || !isToday) return false

        const baseMax = limits.maxTime

        if (baseMax && now > baseMax) return true

        return false
    }, [deliveryDate?.toISO(), limits?.minTime.toISO(), limits?.maxTime?.toISO?.(), isToday, now.toISO()])

    return { isToday, limits, minMaxTime, noSlotsForDate }
}
